import { useCallback, useEffect, useRef, useState } from 'react';

export interface Playback {
  status: 'idle' | 'loading' | 'ready' | 'error';
  playing: boolean;
  currentMs: number;
  /** The element's duration once known, else the fallback passed in. */
  durationMs: number;
  rate: number;
  /** The [start, end] being played on its own, if any. */
  slice: [number, number] | null;
  toggle: () => void;
  seek: (ms: number) => void;
  /** Seek, then play the recording on from there. */
  playFrom: (ms: number) => void;
  setRate: (rate: number) => void;
  playSlice: (startMs: number, endMs: number) => void;
  stop: () => void;
}

const LEAD_IN_MS = 400;
const TAIL_MS = 300;
/** How far the element may sit from a requested seek before it is asked again. */
const SEEK_SLOP_MS = 500;
const SEEK_RETRIES = 3;

/**
 * One recording, played through a single audio element the hook owns. The
 * clock ticks every frame while playing, so words can be lit as they are said.
 */
export function useAudioPlayback(url: string | null, fallbackDurationMs: number): Playback {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const sliceRef = useRef<[number, number] | null>(null);
  const pendingRef = useRef<{ ms: number; tries: number } | null>(null);
  const [media, setMedia] = useState<{
    url: string;
    status: 'ready' | 'error';
    durationMs: number | null;
  } | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentMs, setCurrentMs] = useState(0);
  const [rate, setRateState] = useState(1);
  const [slice, setSlice] = useState<[number, number] | null>(null);

  useEffect(() => {
    if (!url || typeof Audio === 'undefined') {
      return;
    }
    const audio = new Audio();
    audio.preload = 'metadata';
    audioRef.current = audio;
    const onReady = () =>
      setMedia({
        url,
        status: 'ready',
        durationMs: Number.isFinite(audio.duration) ? audio.duration * 1000 : null,
      });
    const onError = () => setMedia({ url, status: 'error', durationMs: null });
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      setPlaying(false);
      sliceRef.current = null;
      setSlice(null);
    };
    const onTime = () => setCurrentMs(audio.currentTime * 1000);
    audio.addEventListener('loadedmetadata', onReady);
    audio.addEventListener('error', onError);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('seeked', onTime);
    audio.src = url;
    return () => {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      audio.removeEventListener('loadedmetadata', onReady);
      audio.removeEventListener('error', onError);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('seeked', onTime);
      audioRef.current = null;
      setPlaying(false);
    };
  }, [url]);

  const current = media && media.url === url ? media : null;
  const status: Playback['status'] = !url ? 'idle' : (current?.status ?? 'loading');

  useEffect(() => {
    if (!playing) {
      return;
    }
    let frame = 0;
    const tick = () => {
      const audio = audioRef.current;
      const pending = pendingRef.current;
      if (audio && pending && !audio.seeking) {
        // Mobile Safari can drop a seek made just before play, or report the old time until it lands.
        const off = Math.abs(audio.currentTime * 1000 - pending.ms) > SEEK_SLOP_MS;
        if (off && pending.tries < SEEK_RETRIES) {
          pending.tries += 1;
          audio.currentTime = pending.ms / 1000;
        } else {
          pendingRef.current = null;
        }
      }
      if (audio && !pendingRef.current) {
        const ms = audio.currentTime * 1000;
        const bounds = sliceRef.current;
        if (bounds && ms >= bounds[1] + TAIL_MS) {
          audio.pause();
          sliceRef.current = null;
          setSlice(null);
        }
        setCurrentMs(ms);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing]);

  const play = useCallback(() => {
    void audioRef.current?.play().catch(() => setPlaying(false));
  }, []);

  const stop = useCallback(() => {
    audioRef.current?.pause();
    sliceRef.current = null;
    setSlice(null);
  }, []);

  const seek = useCallback((ms: number) => {
    const audio = audioRef.current;
    const target = Math.max(0, ms);
    setCurrentMs(target);
    if (audio) {
      pendingRef.current = { ms: target, tries: 0 };
      audio.currentTime = target / 1000;
    }
  }, []);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    sliceRef.current = null;
    setSlice(null);
    if (audio.paused) {
      play();
    } else {
      audio.pause();
    }
  }, [play]);

  const playFrom = useCallback(
    (ms: number) => {
      sliceRef.current = null;
      setSlice(null);
      seek(ms);
      play();
    },
    [play, seek],
  );

  const playSlice = useCallback(
    (startMs: number, endMs: number) => {
      sliceRef.current = [startMs, endMs];
      setSlice([startMs, endMs]);
      seek(Math.max(0, startMs - LEAD_IN_MS));
      play();
    },
    [play, seek],
  );

  const setRate = useCallback((next: number) => {
    setRateState(next);
    if (audioRef.current) {
      audioRef.current.playbackRate = next;
    }
  }, []);

  return {
    status,
    playing,
    currentMs,
    durationMs: current?.durationMs ?? fallbackDurationMs,
    rate,
    slice,
    toggle,
    seek,
    playFrom,
    setRate,
    playSlice,
    stop,
  };
}
